import { formatBRL, centsToReais, type PriceSign } from "@/lib/format-brl";
import { cn } from "@/lib/cn";
import { forwardRef, type HTMLAttributes } from "react";
import { LEADING, TEXT_SIZES, TEXT_WEIGHTS, useTextToneClass, type TextTone } from "./Typography";
import { useElementRef } from "./use-element-ref";

type PriceAmount =
  | { value: number; cents?: never; loading?: boolean }
  | { cents: number; value?: never; loading?: boolean }
  | { loading: true; value?: never; cents?: never };

/** Props of `Price`; `value` in reais or `cents` in minor units, never both; `loading` shows "Calculando…" instead. */
export type PriceProps = PriceAmount &
  Omit<HTMLAttributes<HTMLElement>, "color" | "children"> & {
    sign?: PriceSign;
    free?: boolean | string;
    strike?: boolean;
    size?: keyof typeof TEXT_SIZES;
    weight?: keyof typeof TEXT_WEIGHTS;
    leading?: keyof typeof LEADING;
    tone?: TextTone;
    as?: "span" | "p" | "div" | "strong" | "dd" | "s" | "del";
    loadingLabel?: string;
  };

const DEFAULT_FREE_LABEL = "Grátis";
const DEFAULT_LOADING_LABEL = "Calculando…";

/** Money in BRL through `formatBRL`; `free` swaps a zero for a label, `strike` draws a struck original price. */
export const Price = forwardRef<HTMLElement, PriceProps>(function Price(
  { value, cents, loading = false, loadingLabel = DEFAULT_LOADING_LABEL, sign, free, strike = false, size, weight, leading, tone, as, className, ...rest },
  ref,
) {
  const elementRef = useElementRef(ref);
  const reais = value ?? centsToReais(cents ?? 0);
  const isFree = Boolean(free) && reais === 0 && !strike && !loading;
  const Tag = as ?? (strike && !loading ? "s" : "span");
  const toneClass = useTextToneClass(tone ?? (strike ? "subtle" : isFree ? "secondary" : "inherit"));

  return (
    <Tag
      {...rest}
      ref={elementRef}
      aria-busy={loading || undefined}
      className={cn(
        "whitespace-nowrap tabular-nums",
        size && TEXT_SIZES[size],
        weight && TEXT_WEIGHTS[weight],
        leading && LEADING[leading],
        toneClass,
        strike && !loading && "line-through",
        className,
      )}
    >
      {loading ? (
        loadingLabel
      ) : isFree ? (
        typeof free === "string" ? free : DEFAULT_FREE_LABEL
      ) : (
        formatBRL(reais, { sign })
      )}
    </Tag>
  );
});
